import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import AddPatientModal from "./AddPatientModal";
import "./Searchbar.css";
import { Pagination } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import SkeletonLoader from "../resuableComponents/SkeletonLoader";

const Searchbar = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 30, 50, 100];
  const searchTimeoutRef = useRef(null);

  // Reset to page 1 when limit changes
  useEffect(() => {
    setCurrentPage(1);
  }, [limit]);

  useEffect(() => {
    fetchPatients(currentPage);
  }, [currentPage, limit]);

  // Add this useEffect for scroll behavior
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth" // Optional: adds smooth scrolling
    });
  }, [currentPage]); // This will run whenever currentPage changes

  const fetchPatients = async (page = 1) => {
    const authData = JSON.parse(localStorage.getItem("authData"));
    if (!authData?.accessToken) {
      setError("Authentication required. Please login again.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}get-all-patients`,
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            page: page,
            pageSize: limit,
          },
        }
      );
      setTotalPatients(response.data.totalPatients);
      // console.log("Total Patients:", response.data.totalPatients);

      if (Array.isArray(response.data.result)) {
        setPatients(response.data.result);
        setTotalPages(response.data.totalPages);
      } else {
        setError("Unexpected data format from API.");
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching patients:", err);
      setError("Failed to fetch patients.");
      setLoading(false);
    }
  };

  const searchPatients = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    const authData = JSON.parse(localStorage.getItem("authData"));
    if (!authData?.accessToken) {
      setError("Authentication required. Please login again.");
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}search-patients`,
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            query: query.trim(),
          },
        }
      );

      if (Array.isArray(response.data)) {
        setSearchResults(response.data);
      } else if (Array.isArray(response.data.result)) {
        setSearchResults(response.data.result);
      } else {
        setSearchResults([]);
      }
      setSearchLoading(false);
    } catch (err) {
      console.error("Error searching patients:", err);
      setSearchResults([]);
      setSearchLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim()) {
      setSearchLoading(true);
      // Set a new timeout for debounce (800ms delay)
      searchTimeoutRef.current = setTimeout(() => {
        searchPatients(searchTerm);
      }, 800);
    } else {
      setSearchResults([]);
      setSearchLoading(false);
    }

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const filteredPatients = searchTerm.trim() ? searchResults : patients;

  const handlePatientAdded = (newPatient) => {
    setPatients((prevPatients) => [newPatient, ...prevPatients]);
    setTotalPatients((prev) => prev + 1);
    // Reset to first page to show newly added patient
    setCurrentPage(1);
    fetchPatients(1);
  };

  return (
    <div className="directory-surface">
      <div className="directory-shell">
        <header className="directory-hero">
          <div className="hero-copy">
            <p className="hero-chip">Patient ops</p>
            <h1>
              Patient directory
              <span> at a glance.</span>
            </h1>
            <p>
              Search, review, and add patients without leaving this dashboard.
              Everything stays lightweight and calm.
            </p>
            {/* <div className="hero-insights">
              <div>
                <span>Total patients</span>
                <strong>{totalPatients}</strong>
              </div>
              <div>
                <span>Active page</span>
                <strong>
                  {currentPage}/{totalPages || 1}
                </strong>
              </div>
            </div> */}
          </div>
          <button
            className="primary-action"
            onClick={() => setModalOpen(true)}
          >
            <span>+</span>
            New patient
          </button>
        </header>

        <section className="search-block">
          <div className="search-field">
            <svg
              className="search-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by name or contact…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="result-chip">
            {searchTerm
              ? `Results ${filteredPatients.length}${
                  searchLoading ? "…" : ""
                }`
              : `All patients (${totalPatients})`}
          </div>
        </section>

        <section className="patients-panel">
          <div className="patients-card">
            {(loading || searchLoading) ? (
              <SkeletonLoader variant="patient-list" count={5} />
            ) : error ? (
              <div className="error-state">
                <div className="error-icon">⚠️</div>
                <p>{error}</p>
                <button className="retry-btn" onClick={fetchPatients}>
                  Try Again
                </button>
              </div>
            ) : filteredPatients.length > 0 ? (
              <>
                {filteredPatients.map((patient) => (
                  <NavLink
                    to={`user-info/${patient._id}`}
                    key={patient._id}
                    className="patient-row"
                  >
                    <div className="badge">{patient.name.charAt(0)}</div>
                    <div className="row-body">
                      <h4>{patient.name}</h4>
                      <p>{patient.contact}</p>
                    </div>
                    <div className="row-arrow">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </NavLink>
                ))}

                {!searchTerm && totalPages > 1 && (
                  <div className="cp-pagination" style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
                    <Pagination
                      layout={['total', '-', 'limit', '|', 'pager', 'skip']}
                      total={totalPatients}
                      limit={limit}
                      limitOptions={limitOptions}
                      activePage={currentPage}
                      onChangePage={setCurrentPage}
                      onChangeLimit={(limit) => {
                        setLimit(limit);
                        setCurrentPage(1);
                      }}
                      prev
                      next
                      maxButtons={3}
                      size="md"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h4>No patients found</h4>
                <p>
                  {searchTerm
                    ? "Try adjusting your search terms"
                    : "Start by adding your first patient"}
                </p>
                {!searchTerm && (
                  <button
                    className="empty-cta-btn"
                    onClick={() => setModalOpen(true)}
                  >
                    Add First Patient
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <AddPatientModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onPatientAdded={handlePatientAdded}
        fetchPatients={fetchPatients}
      />
    </div>
  );
};

export default Searchbar;
